/**
 * Promo code config (single source of truth).
 *
 * Imported by:
 *   - src/Components/QuotePage.jsx (top-of-page promo banner)
 *   - src/Components/QuoteReview.jsx (price-line callout)
 *   - src/Components/AgreementView.jsx (signed-agreement pricing block)
 *   - api/quote/submit.js (signed-billing-ticket promo section)
 *
 * Lives under src/ rather than api/ because Create React App refuses
 * imports outside src/. Vercel's serverless function bundler walks
 * the import graph fine in either direction.
 *
 * Add new promo codes here. Each entry is the authoritative copy of:
 *   - amount: dollar discount
 *   - label: short customer-facing tag
 *   - bannerHeading / bannerSubtext: top-of-page banner copy
 *   - agreementLabel: how the credit is itemized on the signed agreement
 *   - ticketDescription: human-readable note in the billing ticket
 *   - appliesTo: 'monthly_first_month' (deducts from first monthly invoice)
 *               | 'invoice_total' (deducts from the full amount due today)
 *
 * The discount is always floored at $0 by applyPromo(); any unused
 * remainder is handled out-of-band by sales. No negative line items
 * are written to QBO.
 *
 * Promo codes are matched case-sensitively (the Sell custom_field value
 * is passed through verbatim from the deal record).
 */

export const PROMOS = {
  '100OFF1': {
    amount: 100,
    label: '$100 off your first month',
    bannerHeading: 'Congratulations! $100 off your first month',
    bannerSubtext: 'Automatically applied to your first invoice.',
    agreementLabel: 'First-month promo credit',
    ticketDescription: 'Apply $100 discount on first invoice.',
    appliesTo: 'monthly_first_month',
  },
  service200comp: {
    amount: 200,
    label: '$200 credit applied',
    bannerHeading: '$200 credit applied',
    bannerSubtext: "Applied to today's invoice. If the credit exceeds the invoice total, the remainder is issued as a credit memo.",
    agreementLabel: '$200 credit applied',
    ticketDescription: 'Apply $200 credit on the invoice, floored at $0. Please issue a credit memo for the remainder.',
    appliesTo: 'invoice_total',
  },
};

export function getPromo(code) {
  if (!code) return null;
  return PROMOS[code] || null;
}

export function applyPromo(amount, code) {
  const promo = getPromo(code);
  if (!promo) return Number(amount || 0);
  return Math.max(0, Number(amount || 0) - promo.amount);
}

export function promoDiscount(amount, code) {
  const promo = getPromo(code);
  if (!promo) return 0;
  return Math.min(promo.amount, Number(amount || 0));
}
